import React, { useContext, useEffect } from "react";

import { FormHelperText, Grid, makeStyles } from "@material-ui/core";
import { Theme } from "@material-ui/core/styles";
import { ErrorMessage, Form, Formik } from "formik";
import * as yup from "yup";

import InputLabel from "../../../components/Form/InputLabel";
import DialogActions from "../../../components/Form/DialogActions";
import TextField from "../../../components/Form/TextField";
import { HospitalsContext } from "../RodaHomepage";
import { Statistics, StatisticsValue } from "../../../types/statistics";
import {
  getStatisticsFields,
  saveStatisticsForHospital,
  updateStatisticsForHospitalByYear,
} from "../../../api/statistics";
import { invalidStatisticsForm } from "../../../constants/messages";

const useStyles = makeStyles((theme: Theme) => ({
  form: { padding: theme.spacing(3.2) },
  error: { color: theme.palette.error.main },
  root: {
    flexGrow: 1,
  },
  hr: {
    height: 1,
    backgroundColor: theme.palette.secondary.dark,
    border: "none",
  },
  disclaimer: {
    color: "#992468",
    fontSize: 24,
  }
}));

const statisticsFormSchema = yup.object().shape({
  year: yup.number(),
  hospitalId: yup.number(),
  values: yup.array().of(
    yup.object().shape({
      id: yup.number().nullable(),
      fieldId: yup.number(),
      fieldText: yup.string(),
      valueText: yup
        .string()
        .max(255, "Molimo unesite do 255 znakova.")
        .nullable(),
    })
  ),
});

type StatisticsFormProps = {
  isNew: boolean;
  statisticsData: Statistics;
  setStatisticsData: (statisticsData: Statistics) => void;
  handleClose: () => void;
};

export default function StatisticsForm({ isNew, statisticsData, setStatisticsData, handleClose }: StatisticsFormProps) {
  const classes = useStyles();
  const { currentHospital } = useContext(HospitalsContext);
  let fieldName: string;

  useEffect(() => {
    async function fetchData() {
      const response = await getStatisticsFields();
      setStatisticsData({ ...statisticsData, values: response.data.values });
    }

    if (!statisticsData.values.length) {
      fetchData();
    }
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={statisticsData}
      validationSchema={statisticsFormSchema}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={async (values) => {
        handleClose();
        if (isNew) {
          if (!isStatisticsBlank(values)) {
            const response = await saveStatisticsForHospital(
              currentHospital,
              values
            );
            setStatisticsData(response.data);
          }
        } else {
          const response = await updateStatisticsForHospitalByYear(
            currentHospital,
            statisticsData.year,
            values
          );
          setStatisticsData(response.data);
        }
      }}
    >
      {({ values, handleChange, handleSubmit, isValid }) => (
        <Form className={classes.form}>
          <div className={classes.root}>
            <Grid container spacing={2} style={{ alignItems: "center" }}>
              {values.values.map((value: StatisticsValue, i) => (
                <React.Fragment key={i}>
                  <div style={{ display: "none" }}>
                    {(fieldName = `values[${i}].valueText`)}
                  </div>
                  <Grid item xs={7}>
                    <InputLabel style={{ marginTop: 0 }}>
                      {value.fieldText}
                    </InputLabel>
                  </Grid>
                  <Grid
                    item
                    xs={5}
                    style={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <TextField
                      onChange={handleChange}
                      value={value.valueText}
                      name={fieldName}
                    />
                  </Grid>
                  {!isValid && (
                    <Grid item xs={9}>
                      <ErrorMessage
                        name={fieldName}
                        component={FormHelperText}
                        className={classes.error}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <hr className={classes.hr}/>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </div>
          <DialogActions
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            message={!isValid && invalidStatisticsForm}
          />
        </Form>
      )}
    </Formik>
  );
}

export function isStatisticsBlank(statisticsData: Statistics): boolean {
  for (const value of statisticsData.values) {
    if (value.valueText && value.valueText.length > 0) {
      return false;
    }
  }

  return true;
}
